import { useState, useContext, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, CheckCircle, XCircle, Sparkles, RefreshCw, Eye, EyeOff } from "lucide-react";

const TYPE_COLORS = {
  Award: "text-verde border-verde/30 bg-verde/10",
  Publication: "text-blue-400 border-blue-400/30 bg-blue-400/10",
  Feature: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  Milestone: "text-purple-400 border-purple-400/30 bg-purple-400/10",
  Education: "text-[#A3A3A3] border-[#404040] bg-ink-800",
};

export default function AccoladesScanTab({ token }) {
  const { API } = useContext(SiteContext);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingApproved, setLoadingApproved] = useState(true);
  const [toggling, setToggling] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPending = () => {
    setLoadingPending(true);
    axios.get(`${API}/accolades/pending`, { headers })
      .then(r => setPending(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingPending(false));
  };

  const fetchApproved = () => {
    setLoadingApproved(true);
    axios.get(`${API}/accolades/all`, { headers })
      .then(r => {
        const sorted = (r.data || []).sort((a, b) => (b.year || "").localeCompare(a.year || ""));
        setApproved(sorted);
      })
      .catch(() => {})
      .finally(() => setLoadingApproved(false));
  };

  const fetchScanStatus = () => {
    axios.get(`${API}/analytics/stats`, { headers })
      .then(r => setScanStatus({
        last: r.data.accolade_last_scan,
        message: r.data.accolade_scan_status,
      })).catch(() => {});
  };

  useEffect(() => { fetchPending(); fetchApproved(); fetchScanStatus(); }, []);

  const handleScan = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/accolades/scan`, {}, { headers });
      toast.success(`Scan complete — ${data.found} new accolade(s) found from ${data.scanned} posts`);
      fetchPending();
      fetchScanStatus();
    } catch {
      toast.error("Scan failed. Make sure posts are loaded first.");
    } finally {
      setScanning(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/accolades/${id}`, { approved: true }, { headers });
      setPending(prev => prev.filter(a => a.id !== id));
      fetchApproved();
      toast.success("Accolade approved — now showing on site.");
    } catch { toast.error("Failed to approve."); }
  };

  const handleReject = async (id) => {
    try {
      await axios.delete(`${API}/accolades/${id}`, { headers });
      setPending(prev => prev.filter(a => a.id !== id));
      toast.success("Accolade rejected and removed.");
    } catch { toast.error("Failed to reject."); }
  };

  const handleToggleHidden = async (id) => {
    setToggling(id);
    try {
      const { data } = await axios.patch(`${API}/accolades/${id}/toggle-hidden`, {}, { headers });
      setApproved(prev => prev.map(a => a.id === id ? { ...a, hidden: data.hidden } : a));
      toast.success(data.hidden ? "Accolade hidden from site" : "Accolade visible on site");
    } catch {
      toast.error("Failed to update.");
    } finally {
      setToggling(null);
    }
  };

  const hiddenCount = approved.filter(a => a.hidden).length;

  return (
    <div className="space-y-4 py-4">
      {/* AI Scanner */}
      <div className="bg-ink-800 border border-ink-600 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-verde mb-1">AI Accolade Scanner</p>
            <p className="font-manrope text-[10px] text-[#525252]">Scans Instagram post captions using Claude AI</p>
          </div>
          <button
            data-testid="accolade-scan-btn"
            onClick={handleScan}
            disabled={scanning}
            className="btn-verde py-2 px-4 flex items-center gap-2 text-[10px] disabled:opacity-60"
          >
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {scanning ? "Scanning..." : "Scan Posts"}
          </button>
        </div>
        {scanStatus?.message && (
          <div className="flex items-start gap-2 mt-2 pt-2 border-t border-ink-600">
            <RefreshCw size={10} className="text-verde mt-1 flex-shrink-0" />
            <div>
              <p className="font-manrope text-[10px] text-verde">{scanStatus.message}</p>
              {scanStatus.last && (
                <p className="font-manrope text-[9px] text-[#525252] mt-0.5">
                  Last: {new Date(scanStatus.last).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pending Review */}
      <div>
        <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] mb-3">
          Pending Review ({pending.length})
        </p>
        {loadingPending ? (
          <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-verde" /></div>
        ) : pending.length === 0 ? (
          <div className="text-center py-6 bg-ink-800 border border-ink-600 rounded-sm">
            <CheckCircle size={20} className="text-verde mx-auto mb-2" />
            <p className="font-manrope text-[#525252] text-xs">No pending accolades.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {pending.map(item => (
              <div key={item.id} data-testid={`pending-accolade-${item.id}`} className="bg-ink-800 border border-purple-400/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-italiana text-lg text-[#525252]">{item.year}</span>
                  <span className={`font-manrope text-[8px] tracking-widest uppercase px-2 py-0.5 rounded border ${TYPE_COLORS[item.type] || TYPE_COLORS["Award"]}`}>{item.type}</span>
                  <span className="font-manrope text-[8px] tracking-widest uppercase px-2 py-0.5 rounded border border-purple-400/30 bg-purple-400/10 text-purple-400">AI</span>
                </div>
                <p className="font-cinzel text-sm text-[#E5E5E5] font-bold">{item.title}</p>
                <p className="font-manrope text-[10px] text-[#525252] mt-1">{item.event}</p>
                {item.source_post_url && (
                  <a href={item.source_post_url} target="_blank" rel="noopener noreferrer" className="font-manrope text-[9px] text-verde hover:text-verde/70 mt-1 block truncate">View source post</a>
                )}
                <div className="flex gap-2 mt-3 pt-3 border-t border-ink-600">
                  <button data-testid={`approve-accolade-${item.id}`} onClick={() => handleApprove(item.id)} className="flex-1 flex items-center justify-center gap-1 bg-verde/10 border border-verde/30 text-verde font-manrope text-[9px] tracking-widest uppercase py-2 rounded-sm hover:bg-verde/20 transition-colors">
                    <CheckCircle size={10} /> Approve
                  </button>
                  <button data-testid={`reject-accolade-${item.id}`} onClick={() => handleReject(item.id)} className="flex-1 flex items-center justify-center gap-1 bg-red-500/10 border border-red-500/30 text-red-400 font-manrope text-[9px] tracking-widest uppercase py-2 rounded-sm hover:bg-red-500/20 transition-colors">
                    <XCircle size={10} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manage Approved Accolades */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252]">
            Approved ({approved.length})
            {hiddenCount > 0 && <span className="text-red-400 ml-2">({hiddenCount} hidden)</span>}
          </p>
        </div>
        {loadingApproved ? (
          <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-verde" /></div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {approved.map(item => (
              <div
                key={item.id}
                data-testid={`approved-accolade-${item.id}`}
                className={`flex items-center gap-3 bg-ink-800 border rounded-sm p-3 transition-opacity ${item.hidden ? "border-red-500/30 opacity-60" : "border-ink-600"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-manrope text-xs text-[#525252] w-12 flex-shrink-0">{item.year}</span>
                    <span className={`font-manrope text-[7px] tracking-widest uppercase px-1.5 py-0.5 rounded border flex-shrink-0 ${TYPE_COLORS[item.type] || TYPE_COLORS["Award"]}`}>{item.type}</span>
                    {item.hidden && <span className="font-manrope text-[7px] tracking-widest uppercase text-red-400 border border-red-400/30 px-1.5 py-0.5 rounded flex-shrink-0">Hidden</span>}
                  </div>
                  <p className="font-manrope text-sm text-[#E5E5E5] truncate mt-1">{item.title}</p>
                  <p className="font-manrope text-[10px] text-[#525252] truncate">{item.event}</p>
                </div>
                <button
                  data-testid={`toggle-accolade-${item.id}`}
                  onClick={() => handleToggleHidden(item.id)}
                  disabled={toggling === item.id}
                  title={item.hidden ? "Show on site" : "Hide from site"}
                  className={`p-2 rounded-sm border transition-colors flex-shrink-0 ${item.hidden
                    ? "border-verde/30 text-verde hover:bg-verde/10"
                    : "border-ink-600 text-[#A3A3A3] hover:text-red-400 hover:border-red-400/30"}`}
                >
                  {toggling === item.id ? <Loader2 size={14} className="animate-spin" /> : item.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
